import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { SITE, whatsappLink } from "@/lib/api";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden mt-24" style={{ background: "var(--color-bg)" }}>
      {/* Top border gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--color-coral)]/40 to-transparent" />

      {/* Ambient glow */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(ellipse, var(--color-coral) 0%, transparent 70%)" }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative">
        <div className="grid lg:grid-cols-12 gap-12">

          {/* BRAND COLUMN */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">

              <div>
                <div className="font-heading text-2xl text-[var(--color-text)]">Dr. Ashish Gupta</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold">
                  Senior Cardiologist · 10+ Years
                </div>
              </div>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-[var(--color-text-muted)] max-w-sm">
              Senior Interventional Cardiologist with 10+ years of clinical excellence. CEO-Founder of
              WellMed Hospitals — premium cardiac care in Gurugram.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <span className="px-3 py-1 rounded-full border border-[var(--color-coral)]/30 bg-[var(--color-coral-light)] text-[var(--color-coral)] text-xs font-semibold">
                ★ {SITE.rating}/5
              </span>
              <span className="text-xs text-[var(--color-text-faint)]">{SITE.reviewCount} verified reviews</span>
            </div>
          </div>

          {/* EXPLORE */}
          <div className="lg:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold mb-5">Explore</div>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/", label: "Home", testId: "footer-link-home" },
                { to: "/about", label: "About", testId: "footer-link-about" },
                { to: "/services", label: "Services", testId: "footer-link-services" },
                { to: "/wellmed", label: "WellMed", testId: "footer-link-wellmed" },
                { to: "/blog", label: "Blog", testId: "footer-link-blog" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-coral)] transition-colors duration-200"
                    data-testid={l.testId}
                    to={l.to}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SERVICES */}
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold mb-5">Services</div>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/services/coronary-angiography-angioplasty", label: "Angiography & Angioplasty" },
                { to: "/services/complex-coronary-interventions", label: "Complex Interventions" },
                { to: "/services/pacemaker-icd-crt", label: "Pacemaker Implantation" },
                { to: "/services/peripheral-renal-interventions", label: "Peripheral Interventions" },
                { to: "/services/preventive-cardiology", label: "Preventive Cardiology" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-coral)] transition-colors duration-200"
                    to={l.to}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT */}
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--color-coral)] font-semibold mb-5">Get In Touch</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[var(--color-coral)] shrink-0" />
                <a
                  href={`tel:${SITE.phone}`}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-coral)] transition-colors"
                  data-testid="footer-phone"
                >
                  {SITE.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[var(--color-coral)] shrink-0" />
                <a
                  href={`mailto:${SITE.email}`}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-coral)] transition-colors break-all"
                  data-testid="footer-email"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[var(--color-coral)] shrink-0 flex-none" />
                <span className="text-[var(--color-text-muted)]">{SITE.address}</span>
              </li>
            </ul>

            <div className="mt-6 text-xs text-[var(--color-text-faint)] leading-relaxed">
              <div className="text-[var(--color-coral)] font-semibold mb-1 tracking-wider uppercase text-[10px]">Hospital</div>
              Open 24×7 · Emergency &amp; Inpatient Care
              <div className="text-[var(--color-coral)] font-semibold mt-3 mb-1 tracking-wider uppercase text-[10px]">
                Dr. Ashish&apos;s Consultation
              </div>
              Mon–Sat: 10:00 AM – 1:00 PM<br />
              Mon–Sat: 5:00 PM – 7:30 PM<br />
              Sunday: Closed
            </div>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="mt-16 pt-8 border-t border-[var(--color-border)] flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-xs text-[var(--color-text-faint)]">
            © {year} Dr. Ashish Gupta. All rights reserved.
          </div>

          {/* Social icons */}
          <div className="flex items-center gap-3">
            {/* WhatsApp */}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-coral)] hover:border-[var(--color-coral)] hover:shadow-[0_0_16px_var(--color-coral-glow)] flex items-center justify-center transition-all duration-300"
              data-testid="social-whatsapp"
              aria-label="WhatsApp"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.52 3.48A11.84 11.84 0 0 0 12.06 0C5.5 0 .14 5.36.14 11.92c0 2.1.55 4.15 1.6 5.97L0 24l6.27-1.65a11.85 11.85 0 0 0 5.79 1.48h.01c6.56 0 11.92-5.36 11.92-11.92 0-3.18-1.24-6.18-3.47-8.43Zm-8.46 18.34h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.98.99-3.62-.24-.37a9.9 9.9 0 0 1-1.52-5.3c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.84 9.84 0 0 1 2.9 7c0 5.45-4.44 9.89-9.89 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.49-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.21 5.08 4.5.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35Z"/>
              </svg>
            </a>
            <a href={SITE.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-coral)] hover:border-[var(--color-coral)] hover:shadow-[0_0_16px_var(--color-coral-glow)] flex items-center justify-center transition-all duration-300 group" aria-label="Instagram">
              <Instagram className="w-4 h-4 group-hover:text-white" />
            </a>
            <a href={SITE.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-coral)] hover:border-[var(--color-coral)] hover:shadow-[0_0_16px_var(--color-coral-glow)] flex items-center justify-center transition-all duration-300 group" aria-label="Facebook">
              <Facebook className="w-4 h-4 group-hover:text-white" />
            </a>
            <a href={SITE.practo} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-coral)] hover:border-[var(--color-coral)] hover:shadow-[0_0_16px_var(--color-coral-glow)] flex items-center justify-center transition-all duration-300 group" aria-label="Practo">
              <span className="font-heading text-sm font-bold group-hover:text-white">P</span>
            </a>
            <a href={SITE.youtube} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] hover:bg-[var(--color-coral)] hover:border-[var(--color-coral)] hover:shadow-[0_0_16px_var(--color-coral-glow)] flex items-center justify-center transition-all duration-300 group" aria-label="YouTube">
              <Youtube className="w-4 h-4 group-hover:text-white" />
            </a>
          </div>
        </div>
        
        {/* RM WEBWORKS ATTRIBUTION */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-[var(--color-text-faint)] tracking-widest uppercase">
            Made with <span className="text-[var(--color-coral)]">❤️</span> by RM Webworks
          </p>
        </div>
      </div>
    </footer>
  );
}
